import { monotonePath } from '../../utils/monotonePath';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import '../../styles/dashboard-peak-trends.css';
import './chart-series-line.css';

interface Point { x: number; y: number; }
interface Props<T extends Point> {
  points: T[];
  comparisonPoints?: T[];
  active: T | null | undefined;
  label: string;
  formatValue: (value: number) => string;
  formatX: (value: number) => string;
  onSelect: (point: T) => void;
  onActivate?: (point: T) => void;
  activateOnClick?: boolean;
  logarithmic?: boolean;
  smooth?: boolean;
  ticks?: number[];
}

/** Shared peak chart geometry, axes, scrub cursor and keyboard point selection. */
export function PeakLinePlot<T extends Point>({ points, comparisonPoints = [], active, label, formatValue, formatX, onSelect, onActivate, activateOnClick = false, logarithmic = false, smooth = false, ticks }: Props<T>) {
  const ref = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(500);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver(([entry]) => { if (entry.contentRect.width > 0) setWidth(entry.contentRect.width); });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  const transform = (value: number) => logarithmic ? Math.log(Math.max(1, value)) : value;
  const all = [...points, ...comparisonPoints].sort((a,b)=>a.x-b.x);
  const first = all[0]?.x ?? 0;
  const last = all[all.length - 1]?.x ?? first;
  const min = transform(first);
  const span = transform(last) - min || 1;
  const max = Math.max(1, ...all.map(p => p.y));
  const x = (value: number) => 45 + (transform(value) - min) / span * (width - 65);
  const y = (value: number) => 220 - value / max * 200;
  const candidates = ticks?.filter(value => value >= first && value <= last) ?? [first, (first + last) / 2, last];
  const labels = candidates.filter((value, index) => index === 0 || index === candidates.length - 1 ||
    (x(value) - x(candidates[index - 1]) >= 65 && x(candidates[candidates.length - 1]) - x(value) >= 65));
  const linePath = (values: T[]) => smooth
    ? monotonePath(values.map(p => ({ x: x(p.x), y: y(p.y) })))
    : values.map((p, i) => `${i ? 'L' : 'M'}${x(p.x)},${y(p.y)}`).join(' ');
  const inspect = (event: PointerEvent<SVGSVGElement>) => {
    if (!all.length) return;
    const position = event.clientX - event.currentTarget.getBoundingClientRect().left;
    onSelect(all.reduce((best, p) => Math.abs(x(p.x) - position) < Math.abs(x(best.x) - position) ? p : best));
  };
  return <svg className="peak-rows__single-plot ltr-island" ref={ref} viewBox={`0 0 ${width} 260`} preserveAspectRatio="none" role="img" aria-label={label}
    onPointerDown={event => { event.currentTarget.setPointerCapture(event.pointerId); inspect(event); }}
    onPointerMove={event => { if (event.pointerType !== 'touch' || event.buttons) inspect(event); }}>
    {[0, .2, .4, .6, .8, 1].map(f => <g key={f}><line x1="45" x2={width - 20} y1={y(max * f)} y2={y(max * f)} className="peak-rows__grid"/><text x="32" y={y(max * f)} textAnchor="end" dominantBaseline="middle">{formatValue(max * f)}</text></g>)}
    <path d={linePath(points)} className="peak-rows__line chart-series-line" />
    {active && <><line x1={x(active.x)} x2={x(active.x)} y1="20" y2="220" className="peak-rows__cursor"/><circle cx={x(active.x)} cy={y(active.y)} r="6" className="peak-rows__active"/></>}
    <path d={linePath(comparisonPoints)} fill="none" stroke="#79b4ee" strokeWidth="2" strokeDasharray="6 4" />
    {all.map((p, index) => <circle key={index} cx={x(p.x)} cy={y(p.y)} r="12" className="peak-rows__hit" role="button" tabIndex={0} aria-label={`${formatX(p.x)} · ${formatValue(p.y)}`}
      onFocus={() => onSelect(p)} onClick={event => {
        let target = p;
        const bounds = ref.current?.getBoundingClientRect();
        if (activateOnClick && event.detail !== 0 && bounds?.width && bounds.height) {
          const px = (event.clientX - bounds.left) / bounds.width * width;
          const py = (event.clientY - bounds.top) / bounds.height * 260;
          const distance = (point: T) => (x(point.x)-px)**2 + (y(point.y)-py)**2;
          target = all.reduce((best, point) => distance(point) < distance(best) ? point : best, p);
        }
        onSelect(target);
        if (activateOnClick) onActivate?.(target);
      }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(p); onActivate?.(p); } }} />)}
    {labels.map((value, index) => <text key={`${index}-${value}`} x={x(value)} y="250" textAnchor={index === 0 ? 'start' : index === labels.length - 1 ? 'end' : 'middle'}>{formatX(value)}</text>)}
  </svg>;
}
